/**
 * /onboarding/welcome — editorial first-impression after household creation
 * (AC-014). Serif heading, short copy, two CTAs. Persists `hasSeenWelcome`
 * in localStorage so returning users skip past it on subsequent visits.
 *
 * - "Capture something" opens the CaptureSheet in Speak mode (the NL flow).
 * - "Take me home" navigates to /home.
 * Both paths mark the welcome step as seen.
 */
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { useAppStore } from '../../lib/store';

const LS_KEY = 'cn:hasSeenWelcome';

export function hasSeenWelcome(): boolean {
  try {
    return window.localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    window.localStorage.setItem(LS_KEY, '1');
  } catch {
    /* silent — non-critical UX storage */
  }
}

export default function WelcomeRoute() {
  const navigate = useNavigate();
  const openCaptureSheet = useAppStore((s) => s.openCaptureSheet);

  // If the user has already seen the welcome step, route them straight home.
  // This guards against bookmarks or back-button navigation into the screen.
  if (hasSeenWelcome()) {
    return <Navigate to="/home" replace />;
  }

  function goHome() {
    markSeen();
    navigate('/home');
  }

  function startCapture() {
    markSeen();
    // Land on Home (so the FAB + capture sheet are mounted in the AppShell),
    // then open the capture sheet immediately. We rely on the global store
    // open state — AppShell mounts CaptureSheet unconditionally.
    navigate('/home');
    openCaptureSheet();
  }

  return (
    <section
      className="mx-auto max-w-xl w-full pt-16 pb-12 px-6"
      data-testid="welcome-screen"
    >
      <header className="mb-10">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink-500 mb-4">
          Welcome
        </p>
        <h1 className="font-serif text-4xl md:text-5xl text-ink-900 leading-tight tracking-tight">
          Welcome to Connoisseur
        </h1>
        <p className="mt-6 font-serif text-lg text-ink-700 italic leading-relaxed max-w-prose">
          Your private journal for places you love. Start by capturing your
          first taste &mdash; or browse the seeded categories.
        </p>
      </header>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Button
          variant="primary"
          size="lg"
          onClick={startCapture}
          data-testid="welcome-capture"
        >
          Capture something
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={goHome}
          data-testid="welcome-home"
        >
          Take me home
        </Button>
        <button
          type="button"
          onClick={goHome}
          className="ml-auto self-end sm:self-center text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
          data-testid="welcome-skip"
        >
          Skip
        </button>
      </div>
    </section>
  );
}
